// Shared team-member filter for the daily-report and fuel-log lists.
//
// `team_member` is free text stamped at submit time. Older rows carry the
// retired roster's ALL-CAPS spellings ("SIMON") while newer rows carry the
// profile name ("Simon"), so an exact-match filter split one person across two
// dropdown options and each option found only half of their reports. These
// helpers treat spellings that differ only by case or spacing as one person.
//
// Stored filter values (session state and saved views) are kept exactly as
// they were saved; matching is case-insensitive, so an old "SIMON" saved view
// keeps working and selects the merged option.

export function teamMemberKey(value: unknown): string {
  if (value == null) return '';
  return String(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

export function sameTeamMember(a: unknown, b: unknown): boolean {
  return teamMemberKey(a) === teamMemberKey(b);
}

// True when the row passes the filter. An empty filter passes every row.
export function matchesTeamMemberFilter(rowValue: unknown, filter: unknown): boolean {
  const key = teamMemberKey(filter);
  return !key || teamMemberKey(rowValue) === key;
}

function isAllCaps(value: string): boolean {
  return value === value.toUpperCase() && value !== value.toLowerCase();
}

// One option per person, sorted case-insensitively. The label shown is the
// spelling written in normal case when one exists ("Simon" over "SIMON");
// among equals, the most frequent spelling, then the alphabetically first.
export function teamMemberOptions(values: Iterable<unknown>): string[] {
  const groups = new Map<string, Map<string, number>>();
  for (const raw of values) {
    if (raw == null) continue;
    const spelling = String(raw).trim().replace(/\s+/g, ' ');
    if (!spelling) continue;
    const key = spelling.toLowerCase();
    let spellings = groups.get(key);
    if (!spellings) {
      spellings = new Map();
      groups.set(key, spellings);
    }
    spellings.set(spelling, (spellings.get(spelling) || 0) + 1);
  }
  const options: string[] = [];
  for (const spellings of groups.values()) {
    const ranked = [...spellings.entries()].sort(
      ([a, aCount], [b, bCount]) =>
        Number(isAllCaps(a)) - Number(isAllCaps(b)) || bCount - aCount || (a < b ? -1 : a > b ? 1 : 0),
    );
    options.push(ranked[0][0]);
  }
  return options.sort((a, b) => {
    const ak = a.toLowerCase();
    const bk = b.toLowerCase();
    return ak < bk ? -1 : ak > bk ? 1 : 0;
  });
}

// The <select> value for a stored filter: the merged option it matches, or the
// stored value unchanged when no loaded row matches it.
export function teamMemberSelectValue(filter: unknown, options: readonly string[]): string {
  const stored = filter == null ? '' : String(filter);
  const key = teamMemberKey(stored);
  if (!key) return '';
  return options.find((option) => teamMemberKey(option) === key) ?? stored;
}
